import {
    getPreparedTypeSelectableAmounts,
    LIQUID_MAX_AMOUNT_ML,
    preparedTypeMaxAmount,
} from "@/common/util/prepared-type-amounts"
import type { Article } from "@/model/backend/article"
import { computed, type Ref } from "vue"

export function useArticleAmountSelect(article: Ref<Article>) {
    const maxSelectableAmount = computed(() => {
        // prepared types are capped in their own display unit: 250 g for flowers, 500 ml for xtracts
        if (article.value.isPreparedType) {
            return preparedTypeMaxAmount(article.value.type)
        }

        // Liquids are capped by volume only, at a fixed 500 ml. Essences are ordered by bottle, so the cap
        // has to be expressed in bottles — the number of bottles itself is deliberately not limited.
        if (article.value.type === "essence") {
            const millilitrePerBottle = article.value.packageAmount ?? 0
            if (millilitrePerBottle <= 0) {
                return 1
            }

            return Math.floor(LIQUID_MAX_AMOUNT_ML / millilitrePerBottle)
        }

        if (article.value.type === "cannabis") {
            return 250
        }

        return 20
    })

    const amountSelectOptions = computed<number[]>(() => {
        if (article.value.isPreparedType) {
            return getPreparedTypeSelectableAmounts(article.value, maxSelectableAmount.value)
        }

        const selectableAmounts = Array.from({ length: maxSelectableAmount.value }, (_, index) => index + 1)
        if (article.value.type === "cannabis") {
            return selectableAmounts.filter(selectableAmount => selectableAmount >= 5)
        }

        return selectableAmounts
    })

    function amountSelectOptionLabel(amount: number) {
        const type = article.value.type
        const unit = article.value.unit

        if (type === "readymade") {
            return amount + " g unzerkleinert"
        }

        if (type === "smalls") {
            return amount + " g unzerkleinert"
        }

        if (type === "milled") {
            return amount + " g zerkleinert"
        }

        if (type === "xtracts") {
            // unlike essences, xtracts are ordered as a total amount that gets filled from 10/30 ml bottles
            return amount + " ml"
        }

        if (type === "essence") {
            return amount * article.value.packageAmount! + " ml"
        }

        if (type === "cannabis") {
            return amount + " g unzerkleinert"
        }

        // everything else — Zubehör, Merch, Selection — is counted in its own unit
        return amount + " " + unit
    }

    function wouldExceedArticleAmountLimit(currentAmount: number, amountToAdd: number): boolean {
        return currentAmount + amountToAdd > maxSelectableAmount.value
    }

    return {
        maxSelectableAmount,
        amountSelectOptions,
        amountSelectOptionLabel,
        wouldExceedArticleAmountLimit,
    }
}
